import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { Mail } from 'lucide-react'
import GenieLogo from '@/components/ui/icon/icons/GenieLogo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/useAuth'
import { authClient } from '@/lib/auth-client'
import { completeAuthRedirect, safeAuthRedirect } from '@/lib/auth-redirect'

const providers = [
  { id: 'google', label: 'Google' },
  { id: 'github', label: 'GitHub' },
  { id: 'twitter', label: 'X' },
] as const

type SocialProvider = typeof providers[number]['id']

export function SignIn() {
  const search = useSearch({ strict: false }) as { redirect?: string; method?: SocialProvider | 'magic-link'; error?: string }
  const redirectTo = safeAuthRedirect(search.redirect)
  const { isLoading, isAuthenticated, signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [usePassword, setUsePassword] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(search.error ? 'Sign-in could not be completed. Please try again.' : '')
  const started = useRef(false)
  const disabled = isLoading || busy !== null
  const callbackURL = new URL(redirectTo, window.location.origin).href
  const errorCallbackURL = new URL(`/login?redirect=${encodeURIComponent(redirectTo)}`, window.location.origin).href

  const social = useCallback(async (provider: SocialProvider) => {
    setBusy(provider); setError('')
    try {
      const result = await authClient.signIn.social({ provider, callbackURL, newUserCallbackURL: callbackURL, errorCallbackURL })
      if (result.error) {
        const label = providers.find(p => p.id === provider)!.label
        throw new Error(result.error.code === 'PROVIDER_NOT_FOUND' ? `${label} sign-in is not configured yet.` : result.error.message || `${label} sign-in failed. Please try again.`)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed. Please try again.')
      setBusy(null)
    }
  }, [callbackURL, errorCallbackURL])

  useEffect(() => {
    if (!isLoading && isAuthenticated) completeAuthRedirect(redirectTo)
  }, [isLoading, isAuthenticated, redirectTo])

  // A provider button in the desktop starts this browser OAuth hand-off once.
  useEffect(() => {
    if (isLoading || isAuthenticated || started.current || search.error || !search.method || search.method === 'magic-link') return
    started.current = true
    void social(search.method)
  }, [isLoading, isAuthenticated, search.method, search.error, social])

  const submit = async () => {
    setBusy('email'); setError('')
    try {
      if (usePassword) {
        await signIn.email({ email: email.trim(), password, rememberMe: true })
        completeAuthRedirect(redirectTo)
      } else {
        const result = await authClient.signIn.magicLink({ email: email.trim(), callbackURL, newUserCallbackURL: callbackURL, errorCallbackURL })
        if (result.error) throw new Error(result.error.message || 'Could not send your sign-in link. Please try again.')
        setSent(true)
      }
    } catch (e) { setError(e instanceof Error ? e.message : 'Sign-in failed. Please try again.') }
    finally { setBusy(null) }
  }

  if (isLoading || isAuthenticated) return <div className="flex min-h-screen items-center justify-center" role="status">{isAuthenticated ? 'Opening Genie…' : 'Checking your session…'}</div>

  return <div className="flex min-h-screen items-center justify-center bg-background px-6 py-8">
    <div className="w-full max-w-[360px] space-y-5">
      <header className="flex flex-col items-center text-center">
        <GenieLogo className="mb-4 h-10 w-10" />
        <h1 className="text-lg font-semibold">Sign in to Genie</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your sessions, models and credits follow your account.</p>
      </header>
      {error && <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
      {sent ? <div className="space-y-4 text-center">
        <Mail className="mx-auto h-6 w-6" />
        <h2 className="font-medium">Check your email</h2>
        <p role="status" className="text-sm text-muted-foreground">We sent a sign-in link to {email.trim()}. Open it to continue. The link expires in 10 minutes.</p>
        <Button variant="outline" className="w-full" onClick={() => setSent(false)}>Use another sign-in method</Button>
      </div> : <>
        <div className="space-y-2">
          {providers.map(provider => <Button key={provider.id} variant="outline" className="h-10 w-full gap-2 whitespace-nowrap" disabled={disabled} onClick={() => void social(provider.id)}>
            {provider.id === 'github' ? <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 19c-4.3 1.3-4.3-2.5-6-3m12 6v-3.9c0-1.1-.4-1.9-1-2.4 3.3-.4 6.7-1.6 6.7-7.3A5.7 5.7 0 0 0 19.2 4 5.3 5.3 0 0 0 19.1.1S17.8-.3 15 1.6a13.4 13.4 0 0 0-7 0C5.2-.3 3.9.1 3.9.1A5.3 5.3 0 0 0 3.8 4a5.7 5.7 0 0 0-1.5 4.4c0 5.7 3.4 6.9 6.7 7.3-.6.5-1 1.3-1 2.4V22" transform="translate(1 1) scale(.9)" /></svg> : <span className="w-4 font-semibold" aria-hidden>{provider.id === 'google' ? 'G' : '𝕏'}</span>}
            {busy === provider.id ? `Opening ${provider.label}…` : `Continue with ${provider.label}`}
          </Button>)}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>
        <form className="space-y-3" onSubmit={e => { e.preventDefault(); void submit() }}>
          <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" autoComplete="email" placeholder="you@example.com" required disabled={disabled} value={email} onChange={e => setEmail(e.target.value)} /></div>
          {usePassword && <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" autoComplete="current-password" required disabled={disabled} value={password} onChange={e => setPassword(e.target.value)} /></div>}
          <Button className="h-10 w-full whitespace-nowrap" disabled={disabled} type="submit">{busy === 'email' ? (usePassword ? 'Signing in…' : 'Sending link…') : (usePassword ? 'Sign in' : 'Email me a magic link')}</Button>
          <button type="button" disabled={disabled} className="w-full text-center text-sm text-muted-foreground hover:underline" onClick={() => { setUsePassword(v => !v); setError('') }}>{usePassword ? 'Use a magic link instead' : 'Use a password instead'}</button>
          {usePassword && <Link to="/forgot-password" search={{ redirect: redirectTo }} className="block text-center text-sm text-muted-foreground hover:underline">Forgot password?</Link>}
        </form>
      </>}
      <p className="text-center text-xs text-muted-foreground">New here? Google, GitHub, X, or a magic link will create your account.</p>
    </div>
  </div>
}
